import React, { useEffect, useRef, useState } from 'react';
import '../../styles/alert-details.scss'; // reuse base fonts; adjust later
import './MessagingEmbed.scss';
import { showToast } from '../../components/toast';
import staticConfig from './static-embed-config.json'; // static mock config

// Trusted origin for postMessage security (set REACT_APP_EMBED_ORIGIN in build env)
const TRUSTED_ORIGIN = process.env.REACT_APP_EMBED_ORIGIN || 'https://www.ministrybrands.com/';

/**
 * MessagingEmbed (POC)
 * Static-mode POC
 * - Uses local JSON (static-embed-config.json) instead of API
 * - Original fetch-based implementation retained below (commented) for later reactivation
 * - Establishes postMessage channel with origin validation
 * - Sends config payload after iframe load
 */
export function MessagingEmbed({
  useApi = false, // default disabled while static config active
  pageType = 'messaging',
  userId,
  customerId,
  theme = 'default',
  locale = 'en-US',
  allowedActions = ['view','send'],
  enableAutoResize = true,
  height = '700px',
  width = '100%',
  className = '',
  style = {},
  onMessage
}) {
  const iframeRef = useRef(null);
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(false); // static load immediate
  const [error, setError] = useState(null);

  // Static config loader (mock API response)
  useEffect(() => {
    setError(null);
    if (staticConfig?.success && staticConfig?.data?.iframeUrl) {
      setSrc(staticConfig.data.iframeUrl);
    } else {
      setError('Static embed config missing iframeUrl');
      showToast('error', 'Static embed config invalid');
    }
  }, [pageType, userId, customerId, theme, locale]);

  /*
   * ORIGINAL API FETCH IMPLEMENTATION (commented for static POC)
   *
   * useEffect(() => {
   *   let abort = false;
   *   async function load() {
   *     if (!useApi) return;
   *     setLoading(true); setError(null);
   *     try {
   *       const res = await fetch('/api/form-builder/messaging/embed', {
   *         method: 'POST',
   *         headers: { 'Content-Type': 'application/json' },
   *         body: JSON.stringify({ pageType, userId, customerId, theme, locale })
   *       });
   *       const data = await res.json();
   *       if (!abort) {
   *         if (data?.success && data?.data?.iframeUrl) setSrc(data.data.iframeUrl);
   *         else {
   *           setError('Failed to load embed config');
   *           showToast('error', 'Failed to load embed config');
   *         }
   *       }
   *     } catch (e) {
   *       if (!abort) {
   *         setError('Network error');
   *         showToast('error', 'Messaging embed request failed');
   *       }
   *     } finally {
   *       !abort && setLoading(false);
   *     }
   *   }
   *   load();
   *   return () => { abort = true; };
   * }, [useApi, pageType, userId, customerId, theme, locale]);
   */

  // PostMessage listener
  useEffect(() => {
    function handleMessage(e) {
      if (e.origin !== TRUSTED_ORIGIN) return; // security gate
      const msg = e.data;
      if (onMessage) onMessage(msg);
      switch (msg?.type) {
        case 'MESSAGING_ACTION_SUCCESS':
          showToast('success', 'Messaging action completed');
          break;
        case 'MESSAGING_ERROR':
          showToast('error', msg?.error?.message || 'Messaging error');
          break;
        case 'MESSAGING_EMBED_RESIZE':
          if (enableAutoResize && msg?.payload?.height && iframeRef.current) {
            iframeRef.current.style.height = msg.payload.height + 'px';
          }
          break;
        default:
          break;
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onMessage, enableAutoResize]);

  // Send configuration to iframe after load
  function sendConfig() {
    try {
      const frame = iframeRef.current;
      if (!frame || !frame.contentWindow) return;
      frame.contentWindow.postMessage({
        type: 'MESSAGING_EMBED_CONFIG',
        config: { theme, locale, allowedActions }
      }, TRUSTED_ORIGIN);
    } catch {}
  }

  return (
    <div className={`me-embed-wrapper ${className}`} style={{ width, ...style }}>
      {loading && (
        <div className='me-loading'>
          <i className='fa fa-spinner fa-spin' /> Loading messaging…
        </div>
      )}
      {error && !loading && (
        <div className='me-error'>
          <i className='fa fa-triangle-exclamation' /> {error}
        </div>
      )}
      {!src && (
        <div className='me-hint'>Static config not loaded. Check JSON file.</div>
      )}
      {src && (
        <iframe
          ref={iframeRef}
          title='HGS Messaging Platform'
          src={src}
          onLoad={sendConfig}
          style={{ width: '100%', height, border: '1px solid #ddd', borderRadius: 8, background: '#fff' }}
          sandbox='allow-scripts allow-same-origin allow-forms allow-popups'
        />
      )}
    </div>
  );
}

export default MessagingEmbed;
